import * as React from "react";
import { useParams, Link } from "react-router-dom";
import Axios from "axios";
import fileDownload from "js-file-download";
import { uppercaseFirstLetter } from "components/utils";
import SparkMD5 from 'spark-md5';

import {
  Space,
  Table,
  Button,
  Skeleton,
  Spin,
  Descriptions,
  Popconfirm,
  message,
  Tag,
  Input,
  Tooltip,
  InputNumber,
  Upload,
  Form,
  Modal,
  Progress,
} from "antd";

import {
  DeleteOutlined,
  PlusOutlined,
  CloudDownloadOutlined,
  UploadOutlined,
  EditOutlined,
  ReloadOutlined,
  WarningFilled
} from "@ant-design/icons";
import {
  DatasetApiCommunicator,
  formatFullDateFromTimeStamp,
  useAmplifyOrgSubscription,
  ModelApiCommunicator,
  UploadCommunicator,
} from "api";
import { addOrUpdateObjectInList, addUniqueToList, sortString } from "utils";
import { getDatasetProcessingStatusColor } from "utils/statusUtils";
import { ListPageLayout } from "layouts";
import { TableListCellItems } from "components/Model/TableListCellItems";
// import { AddModelModal } from "components/AddModelModal";
import { PlusButton } from "components/PlusButton";

const { TextArea } = Input;

/**
 * DatasetDetailPage Component
 * 
 * Displays comprehensive information about a specific dataset and its associated models.
 * Provides functionality for viewing, editing, and managing dataset details including
 * file uploads, processing history, and model management.
 * 
 * Features:
 * - Complete dataset information display
 * - File upload with drag-and-drop functionality
 * - Model management and processing
 * - Processing history tracking
 * - File download capabilities
 * - Real-time updates via Amplify subscriptions
 * - Tag management
 * - Dataset name editing
 * 
 * @returns {JSX.Element} The dataset detail page component
 */
export function DatasetDetailPage() {
  const { datasetId } = useParams();
  return <DatasetFetchResults datasetId={datasetId} />;
}

const tagPlusStyle = {
  background: "#fff",
  borderStyle: "dashed",
};

const tagInputStyle = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: "top",
};

/**
 * DatasetFetchResults Component
 * 
 * Internal component that handles the main dataset detail functionality including
 * data fetching, state management, and user interactions. This component manages
 * all the complex state and API interactions for the dataset detail view.
 * 
 * @param {Object} props - Component props
 * @param {string} props.datasetId - The ID of the dataset to display
 * @returns {JSX.Element} The dataset fetch results component
 */
function DatasetFetchResults({ datasetId }) {
  const [messageApi, contextHolder] = message.useMessage();
  const [datasetApiCommunicator] = React.useState(new DatasetApiCommunicator());
  const [modelApiCommunicator] = React.useState(new ModelApiCommunicator());
  const [uploadCommunicator] = React.useState(new UploadCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");
  const [pendingResultDeleteId, setPendingResultDeleteId] = React.useState("");
  const [addModelModalIsOpen, openAddModelModal] = React.useState(false);
  const [addTagInputVisible, setAddTagInputVisible] = React.useState(false);
  const [addTagInputValue, setAddTagInputValue] = React.useState("");
  const addTagInputRef = React.useRef(null);
  const [smoothnessInput, setSmoothnessInput] = React.useState("50");

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [datasetName, setDatasetName] = React.useState('');

  // const [isDragging, setIsDragging] = React.useState(false);
  /** @type {ReturnType<typeof React.useState<import('models').Dataset>} */
  const [dataset, setDataset] = React.useState();
  /**
   * Map of modelIds with their upload progress
   * as the value. Note that when it's 100%, we wait for the
   * appsync messages saying the upload has been complete to delete it from the map.
   * This is not stored on the model since it's a local indicator
   * to the user's session.
   */
  const [uploadProgressByModelId, setUploadProgressByModelId] = React.useState(
    {}
  );

  useAmplifyOrgSubscription(
    React.useCallback(
      (update) => {
        if (update.type === "dataset" && !loading) {
          setDataset((currDataset) =>
            currDataset.datasetId === update.data.datasetId
              ? update.data
              : currDataset
          );
        }
        if (update.type === "model" && !loading) {
          /** @type {import('models').Model} */
          const newModel = update.data;
          // update the dataset.models property with addition
          setDataset((currDataset) => {
            if (!newModel.datasetIds.includes(currDataset.datasetId)) {
              // This model insert / update does not concern
              // the loaded dataset.
              return currDataset;
            }
            return {
              ...currDataset,
              models: addOrUpdateObjectInList(
                currDataset.models ?? [],
                newModel,
                "modelId"
              ),
            };
          });
          // NOTE:
          // The ideal way to unset the progress status of an uploaded print file
          // would be to wait here
          // Remove the upload progress once the upload has been finalized by validating
          // the payload in this app sync message for the appropriate model.status.
          // However, state updates retrigger this subscription causing some bad loops
          // making that more difficult. The subscription to app sync should probably
          // be moved to a higher component to better control state.
          //
          // setUploadProgressByModelId((currMap) => {
          //   if (newModel.status === "uploaded" && !!currMap[newModel.modelId]) {
          //     return {
          //       ...currMap,
          //       [newModel.id]: undefined,
          //     };
          //     //delete currMap[newModel.modelId]
          //   }
          //   return currMap;
          // });
        }
      },
      [loading]
    )
  );

  const fetchPageData = React.useCallback(async () => {
    if (datasetId) {
      try {
        setLoading(true);
        const res = await datasetApiCommunicator.fetch(datasetId, true);
        // sort the processing history
        res.data.processingHistory.sort(
          (a, b) => b.submittedAt - a.submittedAt
        );
        setDataset(res.data);
      } catch (err) {
        messageApi.error(err.message);
      }
      setLoading(false);
    }
  }, [datasetApiCommunicator, messageApi, datasetId]);

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const errorToast = React.useCallback(
    (text, durationSec = 2) => messageApi.error(text,durationSec),
    [messageApi]
  );

  const downloadModelClick = React.useCallback(
    async (modelId) => {
      try {
        const res = await modelApiCommunicator.fetch(modelId);
        // console.log(res.data);
        setLoading(true);
        const fileDownloadRes = await Axios.get(res.data.downloadUrl, {
          responseType: "blob",
        });
        setLoading(false);
        fileDownload(fileDownloadRes.data, res.data.gcPrintProjectName);
      } catch (err) {
        setLoading(false);
      }
    },
    [modelApiCommunicator, setLoading ]
  );

  const { processingDisabled, models, modelIds } = React.useMemo(() => {
    if (!dataset) {
      return {
        processingDisabled: true,
        models: [],
        modelIds: [],
      };
    }
    return {
      processingDisabled: (dataset.processingHistory && dataset.processingHistory.some(
        (historyItem) =>
          historyItem.status === "processing" ||
          historyItem.status === "submitted"
      )) || (dataset.models && dataset.models.some(model => model.status !== 'uploaded')),
      models: dataset.models || [],
      modelIds: dataset.modelIds || [],
    };
  }, [dataset]);

  React.useEffect(() => {
    if (addTagInputVisible) {
      addTagInputRef.current?.focus();
    }
  }, [addTagInputRef, addTagInputVisible]);

  const handleTagInputChange = React.useCallback(
    async (e) => {
      setAddTagInputValue(e.target.value);
    },
    [setAddTagInputValue]
  );

  const handleTagInputConfirm = React.useCallback(async () => {
    const datasetTags = dataset?.tags || [];
    if (addTagInputValue && !datasetTags.includes(addTagInputValue)) {
      datasetTags.push(addTagInputValue);
      const updateRequest = { datasetId: dataset.datasetId, tags: datasetTags };
      try {
        await datasetApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    }
    setAddTagInputVisible(false);
    setAddTagInputValue("");
  }, [addTagInputValue, dataset, datasetApiCommunicator, messageApi]);

  const showTagInput = React.useCallback(async () => {
    setAddTagInputVisible(true);
  }, [setAddTagInputVisible]);

  const removeTag = React.useCallback(
    async (removeTag) => {
      // console.log("showTagInput Dialog");
      dataset.tags = dataset.tags.filter((tag) => tag !== removeTag);
      const updateRequest = {
        datasetId: dataset.datasetId,
        tags: dataset.tags,
      };
      try {
        await datasetApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    },
    [dataset, datasetApiCommunicator, messageApi]
  );

  const lookupProcessingTypeToolTip = (method) => {
    // Replace this with your actual lookup logic
    const titles = {
      batch: "Uses AWS Batch to process in the cloud",
      step: "Uses AWS Step Functions to allow for asynchronous local processing",
    };

    return titles[method] || "";
  };


  const onUpdateDatasetName = React.useCallback(async (datasetName) => {
    dataset.datasetName = datasetName;
    const updateRequest = { datasetId: dataset.datasetId, datasetName: datasetName };
    try {
      await datasetApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating name " + err.message);
    }

  }, [dataset, datasetApiCommunicator, messageApi]);


  const handleEditNameClick = React.useCallback(() => {
    // console.log('onEditNameClick');
    setDatasetName(dataset.datasetName);
    setIsEditingName(true);
  }, [dataset]);

  const handleNameInputChange = React.useCallback((e) => {
    setDatasetName(e.target.value);
  }, [setDatasetName]);

  const handleNameBlur = React.useCallback(() => {
    setIsEditingName(false);
    if (onUpdateDatasetName) {
      onUpdateDatasetName(datasetName);
    }
  }, [onUpdateDatasetName, datasetName]);

  const handleKeyDown = React.useCallback((e) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    }
  }, [handleNameBlur]);


  const datasetDetailItems = React.useMemo(() => {
    if (!dataset) {
      return [];
    }
    return [
      {
        key: "dataset-name",
        label: "Dataset Name",
        children: 
          <span>
          {isEditingName ? (
            <input
              type="text"
              value={datasetName}
              onChange={handleNameInputChange}
              onBlur={handleNameBlur} 
              onKeyDown={handleKeyDown}
              autoFocus
            />
          ) : (
            <span onClick={handleEditNameClick} style={{ cursor: 'pointer' }}>
              {dataset && dataset.datasetName} <EditOutlined style={{ cursor: 'pointer' }} />
            </span>
          )}
        </span>
      },
      {
        key: "number-of-models",
        label: "# Print Files",
        children: (models || []).length,
      },
      {
        key: "date-added",
        label: "Date Added",
        children: formatFullDateFromTimeStamp(dataset.dateAdded),
      },
      {
        key: "processing-type",
        label: "Processing Type",
        children: (
          <Tooltip
            title={lookupProcessingTypeToolTip(dataset.defaultProcessingMethod)}
          >
            {uppercaseFirstLetter(dataset.defaultProcessingMethod)}
          </Tooltip>
        ),
      },
      {
        key: "dataset-tags",
        label: "Tags",
        span: 2,
        children: (
          <>
            {dataset.tags && dataset.tags.length > 0 ? (
              dataset.tags.map((tag) => (
                <Tag
                  key={tag}
                  color="blue"
                  closable
                  onClose={() => removeTag(tag)}
                >
                  {tag}
                </Tag>
              ))
            ) : (
              <span>No tags</span>
            )}
            {addTagInputVisible ? (
              <Input
                ref={addTagInputRef}
                type="text"
                size="small"
                style={tagInputStyle}
                value={addTagInputValue}
                onChange={handleTagInputChange}
                onBlur={handleTagInputConfirm}
                onPressEnter={handleTagInputConfirm}
              />
            ) : (
              <Tag
                style={tagPlusStyle}
                icon={<PlusOutlined />}
                onClick={showTagInput}
              >
                New Tag
              </Tag>
            )}
          </>
        ),
      },
      {
        key: "ml-models",
        label: "Used In ML Models",
        children: (
          <>
            {dataset.mlModels &&
              dataset.mlModels.map((mlModel, index) => (
                <React.Fragment key={mlModel.mlModelId}>
                  <Link to={`/mlmodels/${mlModel.mlModelId}`}>
                    {mlModel.mlModelName}
                  </Link>
                  {index < dataset.mlModels.length - 1 && ', '}
                </React.Fragment>
              ))}
          </>
        ),
      },
    ];
  }, [dataset, isEditingName, datasetName, handleNameInputChange, handleNameBlur, handleEditNameClick, handleKeyDown, models, addTagInputVisible, addTagInputValue, handleTagInputChange, handleTagInputConfirm, showTagInput, removeTag]);

  const confirmDeleteModel = React.useCallback(
    async (modelId) => {
      setPendingDeleteId("");
      setLoading(true);
      const updatePayload = {
        ...dataset,
        /**
         * Filter out the model id from the dataset update payload
         */
        modelIds: dataset.modelIds.filter((id) => id !== modelId),
      };
      try {
        setDataset((currDataset) => ({
          ...currDataset,
          modelIds,
          models: currDataset.models.filter((item) =>
            modelIds.includes(item.modelId)
          ),
        }));
        await datasetApiCommunicator.update(updatePayload);
        fetchPageData();
      } catch (err) {
        console.log(err);
        messageApi.error("Error deleting model " + err.message);
      }
      setLoading(false);
    },
    [dataset, fetchPageData, modelIds, datasetApiCommunicator, messageApi]
  );

  const confirmDeleteProcessingResult = React.useCallback(
    async (submittedAt) => {
      setPendingResultDeleteId("");
      try {
        setDataset((currDataset) => ({
          ...currDataset,
          processingHistory: currDataset.processingHistory.filter(
            (item) => item.submittedAt !== submittedAt
          ),
        }));
        await datasetApiCommunicator.deleteProcessingResult(
          datasetId,
          submittedAt
        );
      } catch (err) {
        console.log(err);
        messageApi.error("Error deleting processing result " + err.message);
      }
    },
    [datasetApiCommunicator, messageApi, datasetId]
  );

  const processDataset = React.useCallback(async () => {
    // setLoading({ active: true, message: "Sending dataset for processing..." });
    setLoading(true);
    await datasetApiCommunicator.processDataset(
      datasetId,
      parseInt(smoothnessInput)
    );
    fetchPageData();
    setLoading(false);
    // setLoading({ active: false });
  }, [datasetApiCommunicator, datasetId, fetchPageData, smoothnessInput]);

  const columns = React.useMemo(
    () => [
      {
        title: "",
        key: "thumbnail",
        render: (_, r) => (
          r.thumbnailUrl ? (
            <img
              src={r.thumbnailUrl}
              alt={r.modelName || r.modelId}
              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
            />
          ) : null
        ),
      },
      {
        title: "Name",
        key: "modelName",
        sorter: (a, b) => sortString(a.modelName, b.modelName),
        render: (_, r) => (
          <Link to={`/printfiles/${r.modelId}`}>
            {r.modelName || r.modelId}
          </Link>
        ),
      },
      {
        title: "Status",
        key: "status",
        render: (_, r) => {
          if (uploadProgressByModelId && !!uploadProgressByModelId[r.modelId]) {
            return <Progress percent={uploadProgressByModelId[r.modelId]} />;
          }
          if(r.extractedFileCounts && (r.status === 'uploaded' || r.status === 'failed')) {
            if(r.extractedFileCounts.referenceCads === 0 && r.extractedFileCounts.scans === 0) {
              return (
                <span>
                  <TableListCellItems.StatusBadge status={r.status} />
                  <Tooltip title="No parts or scans were detected in the file">
                    <WarningFilled style={{ marginLeft: '8px', color: 'orange', cursor: 'pointer' }} />
                  </Tooltip>
                </span>
              );
            } else if(r.extractedFileCounts.referenceCads !== r.extractedFileCounts.scans) {
              return (
                <span>
                  <TableListCellItems.StatusBadge status={r.status} />
                  <Tooltip title="Mismatched number of parts and scans detected in the file">
                    <WarningFilled style={{ marginLeft: '8px', color: 'orange', cursor: 'pointer' }} />
                  </Tooltip>
                </span>
              );
            } else {
              return <TableListCellItems.StatusBadge status={r.status} />;
            }
          } else {
            return <TableListCellItems.StatusBadge status={r.status} />;
          }
        },
      },
      {
        title: "Part Count",
        key: "part-count",
        render: (_, r) => {
          if(r.status === 'uploaded' || r.status === 'failed') {
            return r.extractedFileCounts?.referenceCads !== undefined ? r.extractedFileCounts.referenceCads : "N/A";
          } else {
            return "";
          }
        },
      },
      {
        title: "Scan Count",
        key: "scan-count",
        render: (_, r) => {
          if(r.status === 'uploaded' || r.status === 'failed') {
            return r.extractedFileCounts?.scans !== undefined ? r.extractedFileCounts.scans : "N/A";
          } else {
            return "";
          }
        },
      },
      {
        title: "Date",
        key: "dateAdded",
        sorter: (a, b) => a.dateAdded - b.dateAdded,
        render: (_, r) => <div>{formatFullDateFromTimeStamp(r.dateAdded)}</div>,
      },
      {
        title: "",
        key: "action",
        render: (_, r) => (
          <Space>
            {r.status === "uploaded" && (
              <CloudDownloadOutlined
                onClick={() => downloadModelClick(r.modelId)}
              />
            )}
            <Popconfirm
              title="Remove this model?"
              description="Are you sure you want to remove this print file from the dataset?"
              open={r.modelId === pendingDeleteId}
              onConfirm={() => confirmDeleteModel(r.modelId)}
              onCancel={() => setPendingDeleteId("")}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined onClick={() => setPendingDeleteId(r.modelId)} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [pendingDeleteId, confirmDeleteModel, uploadProgressByModelId, downloadModelClick]
  );

  const calculateMD5 = async (file) => {
    return new Promise((resolve, reject) => {
      const chunkSize = 2097152; // Read in chunks of 2MB
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();
      let cursor = 0; // current cursor in file

      fileReader.onerror = () => {
        reject('MD5 calculation failed');
      };

      fileReader.onload = (e) => {
        spark.append(e.target.result); // Append array buffer
        cursor += chunkSize; // Move cursor

        if (cursor < file.size) {
          loadNext();
        } else {
          resolve(spark.end()); // Finalize MD5 computation
        }
      };

      const loadNext = () => {
        const fileSlice = file.slice(cursor, cursor + chunkSize);
        fileReader.readAsArrayBuffer(fileSlice);
      };

      loadNext();
    });
  };

  const checkDuplicatesInExistingDataset = React.useCallback(
    async (file) => {
      const checkSum = await calculateMD5(file);
      console.log('checkSum:', checkSum);
      const duplicates = (dataset?.models ?? []).filter(
        (model) => model.checkSum === checkSum
      );
      if(duplicates.length > 0) {
        //return the modelName's of the duplicates
        const duplicateNames = duplicates.map(duplicate => duplicate.modelName);  
        errorToast(`A matching file has already been uploaded with the following name: ${duplicateNames[0]}`, 4);
      }
      return duplicates.length > 0;
    },[dataset, errorToast]
  );

  const didErrorOnExtensionValidation = (name, ext) => {
    if (name.split(".").pop() !== ext) {
      errorToast(`Please upload a .${ext} file.`)
      return true;
    }
    return false;
  }

  /**  @param {React.DragEvent<HTMLDivElement>} e */
  const handleDrop = async (e) => {
    const files = e.dataTransfer.files;
    // Handle the dropped files here
    const md5s = [];
    /** @type {File[]} */
    const validatedFiles = [];
    // Filter list of files through multi-step validation as a prerequisite of the
    // creation / upload process.
    const loadingMessageKey = 'CHECKSUM_CHECK'
    messageApi.loading({ content: "Validating files", duration: 0, key: loadingMessageKey })
    for (const file of files) {
      if (didErrorOnExtensionValidation(file.name, "print")) {
        continue;
      }
      // Check if the file is a duplicate of any other file in the set being dropped
      const hash = await calculateMD5(file);
      if (md5s.includes(hash)) {
        errorToast("One or more files are duplicates of each other. The duplicates will be ignored.", 4);
        continue;
      }
      md5s.push(hash);
      validatedFiles.push(file)
    }
    messageApi.destroy(loadingMessageKey)

    for (const file of validatedFiles) {
      createModelWithPrintFile({ modelName: file.name, partNotes: "" }, file);
    }
  }

  const onCreatePrintFileModalSubmit = (values) => {
    openAddModelModal(false);
    const file = values.upload.file.originFileObj;
    createModelWithPrintFile({ modelName: values.modelName, partNotes: values.partNotes }, file);
  }

  const createModelWithPrintFile = React.useCallback(
    /**
     * Creates a model, updates the state, and regards loading progress
     * for print file upload.
     * 
     * @param {{ partNotes: string, modelName: string }} modelInfo
     * @param {File} file
     * 
     */
    async (modelInfo, file) => {
      const isDuplicate = await checkDuplicatesInExistingDataset(file);
      if (isDuplicate) {
        return;
      }
      try {
        const createRes = await modelApiCommunicator.create({
          gcPrintProjectName: file.name,
          notes: modelInfo.partNotes,
          modelName: modelInfo.modelName,
          datasetIds: [datasetId],
        });
        const newModel = createRes.data

        // Add model to dataset before waiting for update notification
        setDataset(currDataset => ({
          ...currDataset,
          modelIds: addUniqueToList(currDataset.modelIds, newModel.modelId),
          models: addOrUpdateObjectInList(currDataset.models ?? [], newModel, "modelId")
        }))

        // Initialize loading indicator
        setUploadProgressByModelId(curr => ({ ...curr, [newModel.modelId]: 0 }))

        // From the data returned use gcPrintProjectUploadPath to upload the file
        await uploadCommunicator.uploadFile(
          newModel.gcPrintProjectUploadPath,
          file,
          (progressEvent) => {
            // handle update progress
            if (progressEvent.event.lengthComputable) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              console.log("Upload progress:", percent);
              setUploadProgressByModelId((curr) => ({ ...curr, [newModel.modelId]: percent }));
            }
          }
        );
        
        console.log("Finished uploading")

        setUploadProgressByModelId(curr => {
          delete curr[newModel.modelId];
          return curr;
        });
      } catch (e) {
        errorToast("Error uploading file " + file.name);
      }
    },
    [datasetId, modelApiCommunicator, errorToast, uploadCommunicator, checkDuplicatesInExistingDataset]
  )




  const downloadTrainingHistoryResultFileClick = React.useCallback(
    async (url, fileName) => {
      try {
        console.log("Download training history result file", url);
        const fileDownloadRes = await Axios.get(url, {
          responseType: "blob",
        });
        fileDownload(fileDownloadRes.data, fileName);
      } catch (err) {
        errorToast(err.message);
      }
    },
    [errorToast]
  );
  
  const reprocessDatasetModelResult = React.useCallback(
    async (submittedAt, modelId) => {
      setDataset({
        ...dataset,
        processingHistory: dataset.processingHistory.map(historyItem => {
          if (historyItem.submittedAt !== Number(submittedAt)) {
            return historyItem
          }
          return {
            ...historyItem,
            status: 'submitted',
            processResults: historyItem.processResults.map(item => {
              if (item.modelId !== modelId) {
                return item
              }
              return { ...item, status: 'pending', startedAt: null, completedAt: null }
            })
          }
        })
      });      
      try {
        await datasetApiCommunicator.reprocessDatasetModel(dataset.datasetId, submittedAt, modelId);
      } catch (err) {         
        errorToast(err.message);
      }
    },
    [dataset, datasetApiCommunicator, errorToast, setDataset]
  );

  const processingHistoryColumns = React.useMemo(
    () => [
      {
        title: "Submitted",
        dataIndex: "submittedAt",
        key: "submittedAt",
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Started",
        dataIndex: "startedAt",
        key: "startedAt",
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Completed",
        dataIndex: "completedAt",
        key: "completedAt",
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status, record) => {
          // Corrected to use record
          const color = getDatasetProcessingStatusColor(status);
          return (
            <Tooltip title={record.message}>
              {" "}
              {/* Access message from record */}
              <span style={{ color }}>{status}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "PWAM Processor Version",
        dataIndex: "pwamProcessorVersion",
        key: "pwamProcessorVersion",
        render: (_, record) => {
          return record.pwamProcessorVersion || record.pwamVersion;
        },
      },
      {
        title: "Smoothness",
        dataIndex: "smoothness",
        key: "smoothness",
        render: (smoothness) => (smoothness ? smoothness : "N/A"),
      },
      {
        title: "Results",
        dataIndex: "processResults",
        key: "processResults",
        render: (processResults) =>
          processResults ? processResults.length : "N/A",
      },
      {
        title: "Action",
        key: "deleteAction",
        render: (_, r) => (
          <Space>
            <Popconfirm
              title="Delete this result?"
              description="Are you sure to delete this result? This action cannot be undone."
              open={r.submittedAt === pendingResultDeleteId}
              onConfirm={() => confirmDeleteProcessingResult(r.submittedAt)}
              onCancel={() => setPendingResultDeleteId("")}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                onClick={() => setPendingResultDeleteId(r.submittedAt)}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [pendingResultDeleteId, confirmDeleteProcessingResult]
  );

  return (
    <ListPageLayout>
      {contextHolder}
      {loading ? (
        <span>
          <Spin /> {loadingMessage}
        </span>
      ) : (
        <div>
          <Descriptions
            bordered
            items={datasetDetailItems}
            title={
              <div>
                <h3 style={{ paddingLeft: "16px", display: "inline-block" }}>
                  Dataset -
                </h3>
                <span> {dataset && dataset.datasetName}</span>
              </div>
            }
          />
        </div>
      )}
      <Skeleton loading={loading}>
        <div style={{ position: "relative" }}>
          <FileDropzone onDrop={handleDrop}>
            <Table
              columns={columns}
              dataSource={models}
              rowKey="modelId"
              pagination={{ pageSize: 50 }}
              title={() => {
                return (
                  <Space
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <h3 style={{ marginRight: '16px' }}>Print Files</h3>
                      <PlusButton onClick={() => openAddModelModal(true)} />
                    </div>
                    <Popconfirm
                      title="Process this dataset?"
                      description={
                        <>
                          Are you sure you want to process this dataset?
                          <br />
                          This action requires resource intensive processing and
                          cannot be undone.
                          <br />
                          Processing may be lengthy. Please be patient.
                          <br />
                          Smoothness:
                          <InputNumber
                            min={1}
                            max={1000}
                            defaultValue={50}
                            onChange={(e) => {
                              setSmoothnessInput(e);
                            }}
                            style={{
                              marginLeft: "4px",
                              marginTop: "16px",
                              width: "80px",
                            }}
                          />
                        </>
                      }
                      onConfirm={() => processDataset()}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="primary" disabled={processingDisabled}>
                        Process Dataset
                      </Button>
                    </Popconfirm>
                  </Space>
                );
              }}
            />
          </FileDropzone>
        </div>
        <Table
          columns={processingHistoryColumns}
          dataSource={dataset?.processingHistory}
          rowKey="startedAt"
          title={() => <h3>Processing History</h3>}
          style={{ marginTop: 20 }} // Add some space between the tables
          expandable={{
            expandedRowRender: (record) => {
              // console.log('expandedRowRender record', record)
              const columns = [
                {
                  title: "Print File",
                  dataIndex: "modelName",
                  key: "modelName",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "resultStatus",
                  render: (status) => {
                    const color = getDatasetProcessingStatusColor(status);
                    return <span style={{ color }}>{status}</span>;
                  },
                },
                {
                  title: "Started",
                  dataIndex: "startedAt",
                  key: "resultStartedAt",
                  render: (timestamp) =>
                    timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
                },
                {
                  title: "Completed",
                  dataIndex: "completedAt",
                  key: "resultCompletedAt",
                  render: (timestamp) =>
                    timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
                },
                {
                  title: "Result File",
                  dataIndex: "resultFileName",
                  key: "resultFileName",
                },
                {
                  title: "",
                  key: "action",
                  render: (r) =>                    
                    // Inside your component
                    <span>
                      {r.status === "complete" && (
                        <CloudDownloadOutlined
                          onClick={() =>
                            downloadTrainingHistoryResultFileClick(
                              r.resultFilePath,
                              r.resultFileName
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      {(r.status === "complete" || r.status === "processing") && (
                        <ReloadOutlined
                          onClick={() =>
                            reprocessDatasetModelResult(
                              record.submittedAt,
                              r.modelId
                            )
                          }
                          style={{ cursor: 'pointer', marginLeft: '10px' }}
                        />
                      )}
                    </span>
                },
              ];

              const data = record.processResults || [];

              return (
                <Table columns={columns} dataSource={data} pagination={false} />
              );
            },
            rowExpandable: (record) =>
              record.processResults && record.processResults.length > 0,
          }}
        />
      </Skeleton>
      {/* <AddModelModal
        open={addModelModalIsOpen}
        startingModelIdSelection={modelIds}
        onClose={() => openAddModelModal(false)}
        onConfirm={updateModelListForDataSet}
      /> */}

      <CreateModelModal
        open={addModelModalIsOpen}
        onCancel={() => openAddModelModal(false)}
        onSubmit={onCreatePrintFileModalSubmit}
      />
    </ListPageLayout>
  );
}

/**
 * Unstyled utility component for wrapping provided `props.children`
 * to handle drag / drop behavior for file upload. It may be nice to soon
 * make the hovered prompt more flexible for consumers.
 * @param {Object} props
 * @param {React.ReactNode[]} [props.children]
 * @param {(e: React.DragEvent<HTMLDivElement>) => void} [props.onDrop]
 * @param {(e: React.DragEvent<HTMLDivElement>) => void} [props.onDragOver]
 * @param {(e: React.DragEvent<HTMLDivElement>) => void} [props.onDragLeave]
 */
function FileDropzone(props) {
  const [state, setState] = React.useState("");
  /**
   * Drag events will fire for every child, and to avoid the 'pointer-events: none'
   * solution, we keep a static reference to the number of elements that have been dragged over.
   * We thus only update the state on "true" dragleave when this count is 0.
   */
  const dragCounterRef = React.useRef(0);

  /** @param {React.DragEvent<HTMLDivElement>} e */
  const onDragEvent = (e) => {
    e.preventDefault();
    if (e.type === "dragenter") {
      dragCounterRef.current++;
    } else if (e.type === "drop") {
      dragCounterRef.current = 0;
      setState(e.type);
      props.onDrop?.(e);
    } else if (e.type === "dragover") {
      setState(e.type);
      props.onDragOver?.(e);
    } else if (e.type === "dragleave") {
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setState(e.type);
        props.onDragLeave?.(e);
      }
    }
  };

  const additiveClassNames = state === "dragover" ? "hover" : "";

  return (
    <div
      onDragEnter={onDragEvent}
      onDragOver={onDragEvent}
      onDragLeave={onDragEvent}
      onDrop={onDragEvent}
      className={`file-drop-zone ${additiveClassNames}`}
    >
      {props.children}
      {state === "dragover" && (
        <div className="draggable-container-drop-area">
          <div className="draggable-container-drop-area-inner">
            <span>Drop Files Here</span>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateModelModal({ open = false, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = React.useState([]);
  const onFileChange = React.useCallback(
    (fs) => {
      // manually set status for antd's rendering
      for (const f of fs.fileList) {
        f.status = "done";
      }
      setFileList([...fs.fileList]);
      // Set modelName to the name of the first file
      if (fs.fileList[0]) {
        form.setFieldsValue({ modelName: fs.fileList[0].name });
      }
    },
    [form, setFileList]
  );
  return (
    <Modal
      title="Add Print File"
      okText="Add"
      open={open}
      onCancel={onCancel}
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
          setFileList([]);
        }
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
            form.resetFields();
          })
          .catch((info) => {
            console.log("validation failed", info);
          });
      }}
    >
      <Space direction="vertical" style={{ display: "flex" }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ partNotes: "", modelName: "" }}
        >
          <Form.Item
            label="Print Project Upload"
            name="upload"
            rules={[{ required: true, message: "Print file is required" }]}
          >
            <Upload
              accept=".zip,.print"
              maxCount={1}
              customRequest={() => {}}
              onChange={onFileChange}
              fileList={fileList}
            >
              <Button icon={<UploadOutlined />}>Select Print File</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            label="Name"
            name="modelName"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item
            label="Notes"
            name="partNotes"
            rules={[
              {
                required: false,
                message: "Notes",
              },
            ]}
          >
            <TextArea placeholder="Notes" rows={4} />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
}
